import React from 'react';
import { ApolloQueryResult } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/standard-button';
import {
  getAllRemovedPromotions,
  getScheduleChangesDescription,
  getBilledForColumnData,
} from '@css-xp/base/entitlement-utils';
import {
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS,
  sentenceCase,
} from '@atlassian/cssxp-utils-generic';

import {
  FormattedDate,
  DynamicResultTable,
  useSidebar,
  InfoPanel,
  NoDataContentInTable,
  isAccess,
  useWarningModalDialog,
  useFeatureToggle,
} from '@css-xp/base';
import ScheduledPromotionChanges from './scheduled-promotions-changes';
import './schedule-tab.scss';
import { NO_DETAILS_TO_SHOW, isDeletedEntitlement, isOfferingArchived } from '@atlassian/cssxp-utils-entitlement';
import {
  EntitlementDetails,
  Promotion,
  ChargeQuantity,
  SubscriptionScheduleAction,
  LinkedDestinationEntitlement,
} from '@css-xp/graphtypes/src/generated-types-new';
import { Permissions } from '@atlassian/cssxp-typescommon';
import {
  fetchWarningProps,
  getUndoScheduleChangePayload,
  isActiveEntitlementWithoutScheduleCancellation,
} from '../../../components/entitlement-page-utils';
import { PREDICATES } from '../../../../../common/constants';

interface Props {
  changeDate: number | null;
  changeAction?: SubscriptionScheduleAction | null;
  oldValues: ScheduledChangeComparisonFields;
  newValues: ScheduledChangeComparisonFields;
  scheduledPromotions?: Promotion[];
  scheduledQuantity?: ChargeQuantity[];
  scheduledChanges?: EntitlementDetails['scheduledChanges'];
  units?: string | null;
  entitlementNumber?: string | null;
  entitlementId?: string | null;
  productId?: string | null;
  productName?: string | null;
  refetch?: () => Promise<ApolloQueryResult<unknown>>;
  promotions?: Promotion[];
  entitlement?: EntitlementDetails;
  parentProductEntitlements: LinkedDestinationEntitlement[];
}

interface ScheduledChangeComparisonFields {
  offering?: string | null;
  pricingPlan?: string | null;
  billingFrequency?: string | null;
}

const headData = (function () {
  const cells = [
    {
      content: 'Effective',
      width: 30,
    },
    {
      content: 'Description',
      width: 30,
    },
    {
      content: 'Plan',
      width: 30,
    },
    {
      content: 'Pricing',
      width: 30,
    },
    {
      content: 'Billing cycle',
      width: 30,
    },
    {
      content: 'Billed for',
      width: 30,
    },
    {
      content: 'Promotion removed',
      width: 30,
    },
    {
      content: 'Promotion added',
      width: 30,
    },
  ];
  return {
    cells,
  };
})();

export const ScheduledBillingChangesTab = ({
  changeDate,
  changeAction,
  oldValues,
  newValues,
  scheduledPromotions,
  scheduledQuantity,
  units,
  productId,
  refetch,
  promotions,
  entitlement,
  parentProductEntitlements,
}: Props) => {
  const { openSidebar } = useSidebar();
  const [isEnabled] = useFeatureToggle();
  const isWarningModalDialogEnable = isEnabled(ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS);
  const isEntRelationShipFeatureEnable = isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE);
  const { openWarningModalDialog, closeWarningModalDialog } = useWarningModalDialog();
  let isAppArchived = false;
  let isUndoScheduleEnabled = true;
  if (entitlement?.isApp && changeAction === SubscriptionScheduleAction.CANCEL) {
    isAppArchived = isOfferingArchived(entitlement);
    isUndoScheduleEnabled = !isAppArchived && isActiveEntitlementWithoutScheduleCancellation(parentProductEntitlements);
  }

  const createRows = (): Array<any> => {
    const allRemovedPromotions = getAllRemovedPromotions(promotions, scheduledPromotions);

    return [
      {
        key: 'schedule-tab-row',
        cells: [
          {
            content: (
              <>
                {changeDate ? (
                  <FormattedDate date={new Date(changeDate).toISOString()} showTime={true} />
                ) : (
                  <NoDataContentInTable />
                )}
              </>
            ),
          },
          {
            content: getScheduleChangesDescription(changeAction),
          },
          {
            content: (
              <ShowUpdatedText before={sentenceCase(oldValues.offering)} after={sentenceCase(newValues.offering)} />
            ),
          },
          {
            content: (
              <span>
                {' '}
                <ShowUpdatedText
                  before={sentenceCase(oldValues.pricingPlan)}
                  after={sentenceCase(newValues.pricingPlan)}
                />
              </span>
            ),
          },
          {
            content: (
              <span>
                {' '}
                <ShowUpdatedText
                  before={sentenceCase(oldValues.billingFrequency)}
                  after={sentenceCase(newValues.billingFrequency)}
                />
              </span>
            ),
          },
          {
            content:
              scheduledQuantity && scheduledQuantity.length > 0 ? (
                <ShowUpdatedText
                  before={units ? units + ' ' + getBilledForColumnData(productId, oldValues.offering) : ''}
                  after={
                    `${scheduledQuantity[0].quantity}` + ' ' + getBilledForColumnData(productId, newValues.offering)
                  }
                />
              ) : (
                <NoDataContentInTable />
              ),
          },
          {
            content:
              (scheduledPromotions && scheduledPromotions?.length > 0) || (allRemovedPromotions ?? []).length > 0 ? (
                <ScheduledPromotionChanges scheduledPromotions={allRemovedPromotions} isStrike={true} />
              ) : (
                <NoDataContentInTable />
              ),
          },
          {
            content:
              scheduledPromotions && scheduledPromotions?.length > 0 ? (
                <ScheduledPromotionChanges
                  scheduledPromotions={scheduledPromotions.filter((schedule) => schedule.status === 'ACTIVE')}
                  isStrike={false}
                />
              ) : (
                <NoDataContentInTable />
              ),
          },
        ],
      },
    ];
  };

  const constructWarningMessage = () => {
    if (changeAction === SubscriptionScheduleAction.CANCEL) {
      if (isAppArchived) {
        return <p>This change was initiated by the vendor and can’t be undone.</p>;
      }
      return (
        <p>If you schedule other changes, such as to the plan or promotions, it will override the pending changes.</p>
      );
    } else if (changeAction === SubscriptionScheduleAction.UPDATE_CANCEL) {
      return (
        <p>If you schedule other changes, such as to the plan or promotions, it will override the pending changes.</p>
      );
    } else if (
      changeAction === SubscriptionScheduleAction.TRANSFER ||
      changeAction === SubscriptionScheduleAction.UPDATE_TRANSFER
    ) {
      return (
        <p>
          While a transfer is scheduled, you won’t be able to make plan changes, add promotions, rebill, or cancel the
          subscription.
          <br></br>
          <br></br>
          Performing another transfer will override the existing scheduled transfer.
        </p>
      );
    } else if (changeAction === SubscriptionScheduleAction.UPDATE) {
      return (
        <p>
          Scheduling new changes - like cancelling, or changing the plan or promotions - will override these upcoming
          changes.
        </p>
      );
    }
  };

  const linkedEntsTableVisible =
    isEntRelationShipFeatureEnable &&
    (!!entitlement?.relatesFromEntitlements?.length || !!entitlement?.relatesToEntitlements?.length);

  const warningModalDailogVisible = linkedEntsTableVisible && isWarningModalDialogEnable;

  const undoScheduledChangesPayload = getUndoScheduleChangePayload(entitlement, refetch as () => void);

  const undoChangePayloadWithCorrectSidebarType = {
    ...undoScheduledChangesPayload,
    sidebarType: undoScheduledChangesPayload.sidebar,
  };

  const handleConfirmWarningDailog = (sidebarPayload: any) => {
    closeWarningModalDialog();
    openSidebar(sidebarPayload);
  };

  const undoScheduleChangeProps = {
    ...fetchWarningProps(PREDICATES.CANCEL_SCHEDULED_SUBSCRIPTION),
    onConfirm: () => handleConfirmWarningDailog(undoChangePayloadWithCorrectSidebarType),
  };

  const renderActionsOnTop = () => {
    return [
      () => (
        <div className="schedule-warning-message">
          <InfoPanel type="warning" message={constructWarningMessage()} />
        </div>
      ),
      () =>
        !isDeletedEntitlement(entitlement) && isAccess(Permissions.SUBSCRIPTION_BILLING_PERIOD_WRITE) ? (
          <ButtonGroup>
            <Button
              appearance="default"
              style={{ marginBottom: '20px' }}
              data-testId="schedule-undo-btn"
              isDisabled={!isUndoScheduleEnabled}
              onClick={() => {
                warningModalDailogVisible
                  ? openWarningModalDialog(undoScheduleChangeProps)
                  : openSidebar(undoChangePayloadWithCorrectSidebarType);
              }}
            >
              Undo change
            </Button>
          </ButtonGroup>
        ) : (
          <></>
        ),
    ];
  };

  const emptyStateOrderProp = {
    header: '',
    description: NO_DETAILS_TO_SHOW,
  };
  return (
    <>
      <div className="scheduled-changes">
        <DynamicResultTable
          headingText="Scheduled changes"
          head={headData}
          rows={createRows()}
          emptyStateProps={emptyStateOrderProp}
          isPaginationEnabled={false}
          actions={renderActionsOnTop()}
        />
      </div>
    </>
  );
};

/**
 * If old and new text is different, this shows the old text crossed out with an arrow to the new text
 * as a visual representation of the change
 */
export const ShowUpdatedText = ({ before, after }: { before: string; after: string }) => {
  if (!after) after = 'N/A';
  if (!before) before = 'N/A';
  if (before === after) return <>{after}</>;

  const beforeTextStyle = { textDecoration: 'line-through', color: 'gray' };

  return (
    <>
      <span style={beforeTextStyle}>{before}</span> → {after}
    </>
  );
};
